import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { StockLevel } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { apiErrorCode, apiErrorMessage } from '@/services/apiClient';
import {
  useAdjustStockMutation,
  useAdjustmentReasonsQuery,
  useBatchesQuery,
  useIssueStockMutation,
  useReceiveStockMutation,
} from '../api/inventory.api';
import { ExpiryBadge } from './StockStateBadge';

export type MovementMode = 'receive' | 'issue' | 'adjust';

interface StockMovementDrawerProps {
  mode: MovementMode | null;
  level: StockLevel | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

/**
 * Receive, issue or correct stock for one variant at the active branch.
 *
 * The batch fields appear only for a batch-tracked variant, mirroring the API rule exactly: a
 * frame never asks for a lot number, and a contact lens cannot be moved without one.
 */
export function StockMovementDrawer({ mode, level, onClose, onDone }: StockMovementDrawerProps) {
  const { t } = useTranslation();

  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [batchId, setBatchId] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isBatchTracked = level?.isBatchTracked ?? false;
  // Existing lots are only needed when taking stock out or correcting it.
  const batchesQuery = useBatchesQuery(level?.variantId, isBatchTracked && mode !== 'receive');

  // Only an adjustment needs a coded reason; receiving and issuing carry their own meaning.
  const reasonsQuery = useAdjustmentReasonsQuery();
  const receive = useReceiveStockMutation();
  const issue = useIssueStockMutation();
  const adjust = useAdjustStockMutation();
  const pending = receive.isPending || issue.isPending || adjust.isPending;

  useEffect(() => {
    if (!mode) return;

    setQuantity(mode === 'adjust' ? String(level?.quantityOnHand ?? 0) : '');
    setReason('');
    setBatchNumber('');
    setExpiryDate('');
    setBatchId('');
    setReasonId('');
    setError(null);
  }, [mode, level]);

  if (!mode || !level) return null;

  const batchOptions = [
    { value: '', label: t('inventory.movement.selectLot') },
    ...(batchesQuery.data ?? []).map((batch) => ({
      value: batch.id,
      label: `${batch.batchNumber} · ${batch.quantityInBranch ?? 0}${
        batch.expiryDate ? ` · ${batch.expiryDate}` : ''
      }`,
    })),
  ];

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const amount = Number(quantity);
    if (!Number.isInteger(amount) || amount < (mode === 'adjust' ? 0 : 1)) {
      setError(t('inventory.validation.quantityInvalid'));
      return;
    }

    if (isBatchTracked && mode === 'receive' && batchNumber.trim().length === 0) {
      setError(t('inventory.validation.lotRequired'));
      return;
    }
    if (isBatchTracked && mode !== 'receive' && !batchId) {
      setError(t('inventory.validation.lotRequired'));
      return;
    }
    const chosenReason = (reasonsQuery.data ?? []).find((option) => option.id === reasonId);
    if (mode === 'adjust' && !chosenReason) {
      setError(t('inventory.validation.reasonRequired'));
      return;
    }
    if (mode === 'adjust' && chosenReason?.requiresNote && reason.trim().length < 3) {
      setError(t('inventory.validation.noteRequired'));
      return;
    }

    // A named refusal, such as a lot date that disagrees with the lot or a lot already expired, reads in the screen's language.
    const onError = (caught: unknown) => {
      const code = apiErrorCode(caught);
      const known = code ? t(`apiErrors.${code}`, { defaultValue: '' }) : '';
      setError(known || apiErrorMessage(caught) || t('inventory.movement.error'));
    };

    if (mode === 'receive') {
      receive.mutate(
        {
          variantId: level.variantId,
          quantity: amount,
          ...(isBatchTracked
            ? {
                batch: {
                  batchNumber: batchNumber.trim(),
                  ...(expiryDate ? { expiryDate } : {}),
                },
              }
            : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
        {
          onSuccess: () => {
            onDone(t('inventory.movement.received'));
            onClose();
          },
          onError,
        },
      );
      return;
    }

    if (mode === 'issue') {
      issue.mutate(
        {
          variantId: level.variantId,
          quantity: amount,
          ...(batchId ? { batchId } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
        {
          onSuccess: () => {
            onDone(t('inventory.movement.issued'));
            onClose();
          },
          onError,
        },
      );
      return;
    }

    adjust.mutate(
      {
        variantId: level.variantId,
        quantityOnHand: amount,
        ...(batchId ? { batchId } : {}),
        reasonId,
        ...(reason.trim() ? { note: reason.trim() } : {}),
      },
      {
        onSuccess: () => {
          onDone(t('inventory.movement.adjusted'));
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={t(`inventory.movement.${mode}Title`)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="stock-movement" isLoading={pending}>
            {t(`inventory.movement.${mode}Action`)}
          </Button>
        </>
      }
    >
      <form id="stock-movement" onSubmit={handleSubmit} noValidate className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className="border-line bg-canvas/60 rounded-lg border px-4 py-3">
          <p className="text-ink text-sm font-medium">{level.productName}</p>
          <p className="text-muted mt-0.5 text-xs">
            <span className="font-mono" dir="ltr">
              {level.sku}
            </span>
            {level.variantName ? <span className="mx-1.5">· {level.variantName}</span> : null}
          </p>
          <p className="text-muted mt-1 text-xs">
            {t('inventory.fields.onHandNow')}:{' '}
            <span className="text-ink font-medium" dir="ltr">
              {level.quantityOnHand}
            </span>
            {' · '}
            {level.branchName}
          </p>
        </div>

        <Input
          label={t(
            mode === 'adjust' ? 'inventory.fields.countedQuantity' : 'inventory.fields.quantity',
          )}
          required
          type="number"
          min={mode === 'adjust' ? 0 : 1}
          step={1}
          dir="ltr"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />

        {isBatchTracked && mode === 'receive' ? (
          <>
            <Input
              label={t('inventory.fields.batchNumber')}
              required
              dir="ltr"
              placeholder="LOT-2026-A17"
              hint={t('inventory.fields.batchNumberHint')}
              value={batchNumber}
              onChange={(event) => setBatchNumber(event.target.value)}
            />
            <Input
              label={t('inventory.fields.expiryDate')}
              type="date"
              dir="ltr"
              value={expiryDate}
              onChange={(event) => setExpiryDate(event.target.value)}
            />
          </>
        ) : null}

        {isBatchTracked && mode !== 'receive' ? (
          <FormField label={t('inventory.fields.lot')} required>
            {({ id }) => (
              <Select
                id={id}
                options={batchOptions}
                value={batchId}
                onChange={(event) => setBatchId(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        {isBatchTracked && mode !== 'receive' && batchesQuery.data ? (
          <ul className="space-y-1.5">
            {batchesQuery.data.map((batch) => (
              <li key={batch.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-muted font-mono" dir="ltr">
                  {batch.batchNumber} · {batch.quantityInBranch ?? 0}
                </span>
                <ExpiryBadge daysUntilExpiry={batch.daysUntilExpiry} />
              </li>
            ))}
          </ul>
        ) : null}

        {mode === 'adjust' ? (
          <FormField label={t('inventory.fields.reasonCode')} required>
            {({ id }) => (
              <Select
                id={id}
                options={[
                  { value: '', label: t('inventory.fields.selectReason') },
                  ...(reasonsQuery.data ?? []).map((option) => ({
                    value: option.id,
                    label: option.name,
                  })),
                ]}
                value={reasonId}
                onChange={(event) => setReasonId(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        <Input
          label={mode === 'adjust' ? t('inventory.fields.note') : t('inventory.fields.reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </form>
    </Drawer>
  );
}
